/**
 * Категории бренда внутри блока «Приоритет» (§7).
 *
 * Жёсткие требования, которые видно в коде:
 *
 * • Добавление — **только выбором из справочника** (46 позиций, поиск,
 *   счётчики использования). Свободный ввод невозможен — это критерий
 *   приёмки №6; текст в поиске ничего не создаёт, а при попытке «создать
 *   текстом» показывается отказ §12.7 и ссылка «Запросить добавление».
 * • У каждой привязки — свой тир в каждом канале (Р / О), править можно
 *   только тир (§4.3).
 * • Отвязка объясняется строкой-последствием у контрола, а не диалогом (П6):
 *   она неразрушающая и живёт в черновике, откатить её можно одной кнопкой.
 * • Сразу после выбора категории спрашивается тир в активном канале — «позже»
 *   закрывает вопрос без правки (§7).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Brand, BrandCategory, Category, ChannelCode } from '@shared/types.ts'
import {
  Button, Checkbox, ConsequenceLine, IconClose, IconPlus, IconSearch, IconButton, Input,
  Popover, Select, TierBadge,
} from '../../../components/index.js'
import { categoryCell } from '../../../lib/draft-overlay.js'
import { useApp } from '../../../store/app-store.js'
import { TierPicker } from './ChannelBlock.js'
import { canEditValue, enumOptions, isEmptyValue } from './FieldsBlock.js'
import { cardTexts } from './texts.card.js'
import './CategoriesBlock.css'

/** Категории внутри бренда: тир (A+→A→B→C→пусто), затем алфавит (§4.3). */
const TIER_WEIGHT: Record<string, number> = { 'A+': 0, A: 1, B: 2, C: 3 }

function tierWeight(tier: string | null | undefined): number {
  if (!tier) return 9
  return TIER_WEIGHT[tier] ?? 8
}

export interface CategoriesBlockProps {
  brand: Brand
  channel: ChannelCode
  readonly?: boolean
}

export function CategoriesBlock({ brand, channel, readonly = false }: CategoriesBlockProps) {
  const overlay = useApp((state) => state.overlay)
  const bootstrap = useApp((state) => state.bootstrap)
  const attachCategory = useApp((state) => state.attachCategory)
  const editCategoryChannel = useApp((state) => state.editCategoryChannel)
  const addComment = useApp((state) => state.addComment)
  const toast = useApp((state) => state.toast)

  const user = bootstrap?.user ?? null
  const channels = bootstrap?.channels ?? []
  const [pickerOpen, setPickerOpen] = useState(false)
  /** Привязка, у которой сейчас спрашивается тир сразу после выбора (§7). */
  const [askTierFor, setAskTierFor] = useState<number | null>(null)
  const addRef = useRef<HTMLButtonElement | null>(null)

  const canAttach = Boolean(user?.permissions.includes('categories.attach')) && !readonly
  const canDetach = Boolean(user?.permissions.includes('categories.detach')) && !readonly

  const list = useMemo(() => {
    const rows = brand.categories.slice()
    rows.sort((a, b) => {
      const byTier = tierWeight(a.channels[channel]?.tier) - tierWeight(b.channels[channel]?.tier)
      return byTier !== 0 ? byTier : a.name.localeCompare(b.name, 'ru')
    })
    return rows
  }, [brand.categories, channel])

  const visibleCount = list.filter((item) => !overlay.detached.has(item.id)).length
  const attachedIds = new Set(brand.categories.map((item) => item.categoryId))

  async function attach(category: Category): Promise<void> {
    setPickerOpen(false)
    await attachCategory(brand.id, category.id)
    const fresh = useApp.getState().brandsById.get(brand.id)
    const created = fresh?.categories.find((item) => item.categoryId === category.id)
    if (created) setAskTierFor(created.id)
  }

  async function requestCategory(name: string): Promise<void> {
    await addComment(brand.id, cardTexts.categories.requestBody(name), channel)
    toast('info', cardTexts.categories.requestSent)
    setPickerOpen(false)
  }

  return (
    <div className="card-cats">
      <div className="card-cats__head">
        <span className="card-cats__title t-label">{cardTexts.categories.title(visibleCount)}</span>
        <Button
          ref={addRef}
          variant="ghost"
          size="m"
          iconBefore={<IconPlus size={16} />}
          onClick={() => setPickerOpen((previous) => !previous)}
          disabled={!canAttach}
          aria-expanded={pickerOpen}
          aria-haspopup="dialog"
          aria-label={cardTexts.categories.addLabel}
        >
          {cardTexts.categories.add}
        </Button>
      </div>

      {!canAttach && !readonly ? (
        <ConsequenceLine>{cardTexts.categories.attachDenied}</ConsequenceLine>
      ) : null}

      {list.length === 0 ? (
        <p className="card-cats__empty t-meta">{cardTexts.categories.emptyTitle}</p>
      ) : (
        <ul className="card-cats__list">
          {list.map((item) => (
            <CategoryRow
              key={item.id}
              brand={brand}
              category={item}
              channel={channel}
              channels={channels}
              canDetach={canDetach}
              readonly={readonly}
              askTier={askTierFor === item.id}
              onAskTierDone={() => setAskTierFor(null)}
              onTier={(code, tier) => void editCategoryChannel(brand.id, item.id, code, { tier })}
            />
          ))}
        </ul>
      )}

      <CategoryPicker
        open={pickerOpen}
        anchor={addRef.current}
        onClose={() => setPickerOpen(false)}
        attachedIds={attachedIds}
        onPick={(category) => void attach(category)}
        onRequest={(name) => void requestCategory(name)}
      />
    </div>
  )
}

// ──────────────────────────────── строка категории ───────────────────────────
interface CategoryRowProps {
  brand: Brand
  category: BrandCategory
  channel: ChannelCode
  channels: { code: ChannelCode; name: string; shortName: string }[]
  canDetach: boolean
  readonly: boolean
  askTier: boolean
  onAskTierDone: () => void
  onTier: (channel: ChannelCode, tier: string | null) => void
}

function CategoryRow({
  brand, category, channel, channels, canDetach, readonly, askTier, onAskTierDone, onTier,
}: CategoryRowProps) {
  const overlay = useApp((state) => state.overlay)
  const bootstrap = useApp((state) => state.bootstrap)
  const detachCategory = useApp((state) => state.detachCategory)
  const [confirming, setConfirming] = useState(false)

  const detached = overlay.detached.has(category.id)
  const isDraft = category.isDraft || overlay.draftCategories.has(category.id)
  const activeChannelName = channels.find((item) => item.code === channel)?.name ?? ''

  const classes = ['card-cat']
  if (detached) classes.push('card-cat--detached')
  if (isDraft || detached) classes.push('card-cat--draft')

  return (
    <li className={classes.join(' ')}>
      <div className="card-cat__row">
        <span className="card-cat__name t-cat t-truncate">{category.name}</span>

        {/* Только активный канал. Раньше в строке стояли оба — «Розница» и
            «Опт» рядом, — и вкладка сверху ни на что не влияла: правка уходила
            не в тот канал, который человек выбрал. Канал задаётся вкладкой. */}
        {(() => {
          const cell = categoryCell<string>(overlay, category, channel, 'tier')
          const present = Boolean(brand.channels[channel]?.isPresent)
          return (
            <span className={cell.isDraft ? 'card-cat__tier card-cat__tier--draft' : 'card-cat__tier'}>
              <CategoryTierSelect
                value={typeof cell.value === 'string' ? cell.value : null}
                label={`${category.name} · ${cardTexts.categories.tierIn(activeChannelName)}`}
                disabled={readonly || !present || !canEditValue(bootstrap?.user ?? null, isEmptyValue(cell.published)).allowed}
                onChange={(next) => onTier(channel, next)}
              />
            </span>
          )
        })()}

        <IconButton
          label={`${cardTexts.categories.detach}: ${category.name}`}
          icon={<IconClose size={16} />}
          variant="ghost"
          size="m"
          disabled={!canDetach || detached}
          onClick={() => setConfirming(true)}
        />
      </div>

      {detached ? (
        <p className="card-cat__mark t-meta">{cardTexts.categories.detachedMark}</p>
      ) : isDraft ? (
        <p className="card-cat__mark t-meta">{cardTexts.categories.draftMark}</p>
      ) : null}

      {confirming ? (
        <div className="card-cat__confirm">
          <ConsequenceLine>
            {cardTexts.categories.detachConsequence(category.name, brand.name)}
          </ConsequenceLine>
          <div className="card-cat__confirm-actions">
            <Button
              variant="secondary"
              size="m"
              onClick={() => {
                setConfirming(false)
                void detachCategory(brand.id, category.id)
              }}
            >
              {cardTexts.categories.detachConfirm}
            </Button>
            <Button variant="ghost" size="m" onClick={() => setConfirming(false)}>
              {cardTexts.categories.detachCancel}
            </Button>
          </div>
        </div>
      ) : null}

      {askTier ? (
        <div className="card-cat__ask">
          <span className="card-cat__ask-label t-label">
            {cardTexts.categories.tierPromptLabel(activeChannelName)}
          </span>
          <TierPicker
            value={null}
            options={(bootstrap?.enums.tier ?? []).filter((item) => item.isActive).map((item) => item.value)}
            label={`${category.name} · ${cardTexts.categories.tierIn(activeChannelName)}`}
            onChange={(next) => {
              onTier(channel, next)
              onAskTierDone()
            }}
          />
          <Button variant="ghost" size="m" onClick={onAskTierDone}>
            {cardTexts.categories.tierLater}
          </Button>
        </div>
      ) : null}
    </li>
  )
}

interface CategoryTierSelectProps {
  value: string | null
  label: string
  disabled: boolean
  onChange: (value: string | null) => void
}

export function CategoryTierSelect({ value, label, disabled, onChange }: CategoryTierSelectProps) {
  const [open, setOpen] = useState(false)
  const enums = useApp((state) => state.bootstrap?.enums)
  const options = enumOptions(enums, 'tier').map((option) => ({
    ...option,
    badge: <TierBadge tier={option.value} />,
  }))

  return (
    <Select
      value={value}
      options={options}
      onChange={onChange}
      open={open}
      onOpenChange={setOpen}
      label={label}
      size="m"
      emptyLabel={cardTexts.value.tierEmpty}
      clearLabel={cardTexts.value.clear}
      disabled={disabled}
    />
  )
}

// ──────────────────────────────── комбобокс справочника ──────────────────────
export interface CategoryPickerProps {
  open: boolean
  anchor: HTMLElement | null
  onClose: () => void
  /** Уже привязанные к бренду категории: показываем, но выбрать нельзя. */
  attachedIds: Set<number>
  onPick: (category: Category) => void
  onRequest: (name: string) => void
  /** Множественный выбор: нужен в модалке создания бренда. */
  multiple?: boolean
  selectedIds?: Set<number>
}

/**
 * Поиск по справочнику категорий (46 позиций, счётчики использования).
 * Свободный ввод не создаёт категорию никогда: строка поиска только фильтрует,
 * а при отсутствии совпадений предлагает «Запросить добавление» (§7, §12.7).
 */
export function CategoryPicker({
  open, anchor, onClose, attachedIds, onPick, onRequest, multiple = false, selectedIds,
}: CategoryPickerProps) {
  const categories = useApp((state) => state.bootstrap?.categories ?? [])
  const [query, setQuery] = useState('')
  const [requesting, setRequesting] = useState(false)
  const [denied, setDenied] = useState(false)
  const searchRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) {
      setQuery('')
      setRequesting(false)
      setDenied(false)
      return
    }
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [open])

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return categories
      .filter((item) => item.status === 'active')
      .filter((item) => needle === '' || item.name.toLowerCase().includes(needle))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [categories, query])

  return (
    <Popover
      open={open}
      anchor={anchor}
      onClose={onClose}
      role="dialog"
      placement="bottom-end"
      minWidth={280}
      maxWidth={280}
      className="card-picker"
    >
      <div className="card-picker__search">
        <Input
          ref={searchRef}
          value={query}
          size="m"
          iconBefore={<IconSearch size={16} />}
          placeholder={cardTexts.categories.searchPlaceholder}
          aria-label={cardTexts.categories.search}
          onChange={(event) => {
            setQuery(event.target.value)
            setDenied(false)
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            const first = found[0]
            if (first && !attachedIds.has(first.id)) {
              onPick(first)
              return
            }
            /* Текстом категорию создать нельзя — критерий приёмки №6. */
            setDenied(true)
          }}
        />
      </div>

      {denied ? (
        <p className="card-picker__denied t-meta">{cardTexts.categories.freeInputDenied}</p>
      ) : null}

      <ul className="card-picker__list" aria-label={cardTexts.categories.pickerLabel}>
        {found.map((category) => {
          const attached = attachedIds.has(category.id)
          const picked = selectedIds?.has(category.id) ?? false
          return (
            <li key={category.id}>
              {multiple ? (
                <div className="card-picker__option card-picker__option--multi">
                  <Checkbox
                    checked={picked}
                    onChange={() => onPick(category)}
                    label={category.name}
                  />
                  <span className="card-picker__usage t-meta">
                    {cardTexts.categories.usage(category.usageCount ?? 0)}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="card-picker__option t-cell"
                  disabled={attached}
                  aria-disabled={attached || undefined}
                  onClick={() => onPick(category)}
                >
                  <span className="card-picker__name t-truncate">{category.name}</span>
                  <span className="card-picker__usage t-meta">
                    {attached
                      ? cardTexts.categories.attached
                      : cardTexts.categories.usage(category.usageCount ?? 0)}
                  </span>
                </button>
              )}
            </li>
          )
        })}
      </ul>

      {found.length === 0 ? (
        <p className="card-picker__nothing t-meta">{cardTexts.categories.nothingFound}</p>
      ) : null}

      <div className="card-picker__foot">
        {requesting ? (
          <CategoryRequest
            initial={query}
            onCancel={() => setRequesting(false)}
            onSubmit={(name) => {
              onRequest(name)
              setRequesting(false)
            }}
          />
        ) : (
          <button type="button" className="card-picker__request t-meta" onClick={() => setRequesting(true)}>
            {cardTexts.categories.requestLink}
          </button>
        )}
      </div>
    </Popover>
  )
}

interface CategoryRequestProps {
  initial: string
  onCancel: () => void
  onSubmit: (name: string) => void
}

function CategoryRequest({ initial, onCancel, onSubmit }: CategoryRequestProps) {
  const [name, setName] = useState(initial)

  return (
    <div className="card-picker__request-form">
      <Input
        value={name}
        size="m"
        autoFocus
        placeholder={cardTexts.categories.requestPlaceholder}
        aria-label={cardTexts.categories.requestTitle}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && name.trim() !== '') {
            event.preventDefault()
            onSubmit(name.trim())
          }
        }}
      />
      <div className="card-picker__request-actions">
        <Button variant="secondary" size="m" disabled={name.trim() === ''} onClick={() => onSubmit(name.trim())}>
          {cardTexts.categories.requestSubmit}
        </Button>
        <Button variant="ghost" size="m" onClick={onCancel}>
          {cardTexts.categories.detachCancel}
        </Button>
      </div>
    </div>
  )
}
